import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { AdminMeta } from '../layouts/v2/components/types';

import { adminFetch } from './adminFetch';

export type WorkflowActor = v4.WorkflowActor;

async function responseErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  const body = await response.text();
  if (!body) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    return typeof parsed.message === 'string' ? parsed.message : fallback;
  } catch {
    return body;
  }
}

export const toWorkflowActor = (
  account:
    | {
        homeAccountId?: string;
        localAccountId?: string;
        name?: string;
        username?: string;
      }
    | null
    | undefined,
): WorkflowActor => ({
  id: account?.homeAccountId || account?.localAccountId,
  name: account?.name,
  username: account?.username,
});

export type MailboxRoute = {
  id: string;
  type: 'MAILBOX_ROUTE';
  mailbox: string;
  workflowId: string;
  enabled: boolean;
  updatedAt: string;
};

export type MailAutomationSettings = {
  attachmentParsingEnabled: boolean;
  updatedAt: string | null;
};

export const getWorkflowsCollection = async (
  token: string,
): Promise<v4.WorkflowHeadSpec[]> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/workflows`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch workflows');
  }

  return response.json() as Promise<v4.WorkflowHeadSpec[]>;
};

export const getWorkflow = async (
  token: string,
  workflowId: string,
): Promise<{
  head: v4.WorkflowHeadSpec;
  versions: v4.WorkflowVersionSpec[];
}> => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(workflowId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch workflow');
  }

  return response.json();
};

export const getMeta = async (token: string): Promise<AdminMeta> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/workflow-meta`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch workflow meta');
  }

  return response.json();
};

export const updateWorkflowVersion = async (
  token: string,
  workflowId: string,
  version: number,
  update: {
    spec?: v4.WorkflowSpec;
    title?: string;
    description?: string;
    note?: string;
    actor?: WorkflowActor | null;
  },
) => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(workflowId)}/versions/${version}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(update),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to update workflow version');
  }

  return response.json();
};

export const createWorkflow = async (
  token: string,
  input: {
    workflowId: string;
    title: string;
    description?: string;
    trigger?: string;
    actor?: WorkflowActor | null;
  },
) => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/workflows`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to create workflow');
  }

  return response.json();
};

export const activateWorkflowVersion = async (
  token: string,
  input: {
    workflowId: string;
    version: number;
    expectedActiveVersion?: number | null;
    actor?: WorkflowActor | null;
  },
) => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(input.workflowId)}/versions/${
      input.version
    }/activate`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        expectedActiveVersion: input.expectedActiveVersion,
        actor: input.actor,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      await responseErrorMessage(
        response,
        'Failed to activate workflow version',
      ),
    );
  }

  return response.json();
};

export const createWorkflowVersion = async (
  token: string,
  id: string,
  input: {
    basedOn: number;
    actor?: WorkflowActor | null;
  },
) => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(id)}/versions`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw new Error(
      await responseErrorMessage(response, 'Failed to create workflow version'),
    );
  }

  return response.json();
};

export const getWorkflowVersionValidation = async (
  token: string,
  workflowId: string,
  version: number,
): Promise<v4.WorkflowValidationResult> => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(
      workflowId,
    )}/versions/${version}/validation`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to validate workflow version');
  }

  return response.json() as Promise<v4.WorkflowValidationResult>;
};

export const deleteWorkflowVersion = async (
  token: string,
  workflowId: string,
  version: number,
  input: { actor?: WorkflowActor | null } = {},
) => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(workflowId)}/versions/${version}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );

  if (!response.ok) {
    throw new Error(
      await responseErrorMessage(response, 'Failed to delete workflow version'),
    );
  }

  return response.json();
};

export const compareWorkflowVersions = async (
  token: string,
  workflowId: string,
  toVersion: number,
): Promise<v4.WorkflowComparison> => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/workflows/${encodeURIComponent(
      workflowId,
    )}/compare?toVersion=${toVersion}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to compare workflow versions');
  }

  return response.json();
};

export const getMailboxRoutes = async (
  token: string,
): Promise<MailboxRoute[]> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/mailbox-routes`,
    {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch mailbox routes');
  }
  return response.json();
};

export const saveMailboxRoute = async (
  token: string,
  input: { mailbox: string; workflowId: string; enabled?: boolean },
): Promise<MailboxRoute> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/mailbox-routes`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to save mailbox route');
  }
  return response.json();
};

export const deleteMailboxRoute = async (token: string, mailbox: string) => {
  const response = await adminFetch(
    `${
      process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT
    }/mailbox-routes/${encodeURIComponent(mailbox)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new Error('Failed to delete mailbox route');
  }
};

export const getMailAutomationSettings = async (
  token: string,
): Promise<MailAutomationSettings> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/mail-automation/settings`,
    {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch mail automation settings');
  }
  return response.json() as Promise<MailAutomationSettings>;
};

export const saveMailAutomationSettings = async (
  token: string,
  settings: Pick<MailAutomationSettings, 'attachmentParsingEnabled'>,
): Promise<MailAutomationSettings> => {
  const response = await adminFetch(
    `${process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT}/mail-automation/settings`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to save mail automation settings');
  }
  return response.json() as Promise<MailAutomationSettings>;
};
